import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  FileText, Image as ImageIcon, FileQuestion, Eye, X, Download,
  RefreshCw, Inbox, AlertTriangle,
} from "lucide-react";

/**
 * Inline KYC document viewer (participant-console-005).
 *
 * A STANDALONE, self-contained component: the consolidated review pane
 * (participant-console-004) imports it into a slot. It owns nothing in the
 * console shell or routing — it takes a `userId` prop and renders that
 * participant's KYC document list with an inline view (image / PDF) and a
 * per-document open.
 *
 * Backend contract (participant-console-005, partner_console_docs.py):
 *   GET /api/partner/console/participants/:userId/documents          → list (metadata)
 *   GET /api/partner/console/participants/:userId/documents/:docId   → bytes (inline)
 *
 * Both are gated by `require_partner`, a per-partner rate limit, and the
 * console's member scope: the id must be a client member (CAPMARKETS-253 —
 * agreement state no longer matters here). A non-client or unknown id returns
 * 404; a client member who has not signed the data-transfer consent returns
 * 403 with the reason. The bytes endpoint is fetched
 * as a blob (the axios `api` instance attaches the partner bearer token) and
 * rendered through an object URL so the document never touches a query string.
 */

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

const prettyType = (t) =>
  (t || "document")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const statusPill = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("approved")) return "bg-emerald-100 text-emerald-900 border-emerald-300";
  if (s.includes("reject")) return "bg-rose-100 text-rose-900 border-rose-300";
  if (s.includes("pending")) return "bg-amber-100 text-amber-900 border-amber-300";
  return "bg-slate-100 text-slate-700 border-slate-300";
};

// Best-effort kind from the file name so the row icon + inline render mode are
// chosen without trusting a server-sent content type in the list payload.
const kindFromName = (fileName) => {
  const ext = (fileName || "").split(".").pop().toLowerCase();
  if (["png", "jpg", "jpeg", "gif", "webp", "heic", "tiff", "tif"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "other";
};

const KindIcon = ({ kind, size = 16 }) => {
  if (kind === "image") return <ImageIcon size={size} className="text-slate-400" />;
  if (kind === "pdf") return <FileText size={size} className="text-slate-400" />;
  return <FileQuestion size={size} className="text-slate-400" />;
};

const KycDocViewer = ({ userId }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The currently-opened document: { doc, url, kind } or null. The object URL
  // is revoked when the viewer closes / switches documents to avoid leaks.
  const [active, setActive] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeError, setActiveError] = useState(null);
  const activeUrlRef = useRef(null);

  const revokeActiveUrl = useCallback(() => {
    if (activeUrlRef.current) {
      URL.revokeObjectURL(activeUrlRef.current);
      activeUrlRef.current = null;
    }
  }, []);

  const fetchDocs = useCallback(async () => {
    if (!userId) {
      // No participant in context — clear out and stop loading so the UI shows
      // an empty state instead of being stuck on the spinner.
      setDocs([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(
        `/partner/console/participants/${userId}/documents`,
      );
      setDocs(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 403) {
        // CAPMARKETS-253: the member has not signed the data-transfer consent.
        // The server says so in words; render them, so the reading is never
        // "missing KYC" again.
        setError(
          err?.response?.data?.detail ||
            "This member has not yet signed the data-transfer consent. Their documents become available once they sign it.",
        );
      } else if (err?.response?.status === 404) {
        // Post-253 a 404 here is not a scope refusal for a listed member: the
        // review pane above already loaded them through the same predicate.
        // It means the member changed or vanished mid-session.
        setError("This participant could not be found. Reload the review.");
      } else {
        setError("Could not load documents. Please try again.");
      }
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  // Revoke any outstanding object URL on unmount.
  useEffect(() => () => revokeActiveUrl(), [revokeActiveUrl]);

  const openDoc = useCallback(async (doc) => {
    revokeActiveUrl();
    setActive({ doc, url: null, kind: kindFromName(doc.file_name) });
    setActiveLoading(true);
    setActiveError(null);
    try {
      const resp = await api.get(
        `/partner/console/participants/${userId}/documents/${doc.id}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      activeUrlRef.current = url;
      // Prefer the blob's actual MIME type when present.
      const type = resp.data?.type || "";
      const kind = type.startsWith("image/")
        ? "image"
        : type === "application/pdf"
          ? "pdf"
          : kindFromName(doc.file_name);
      setActive({ doc, url, kind });
    } catch (err) {
      setActiveError(
        err?.response?.status === 404
          ? "This document is no longer available."
          : "Could not load this document.",
      );
    } finally {
      setActiveLoading(false);
    }
  }, [userId, revokeActiveUrl]);

  const closeDoc = useCallback(() => {
    revokeActiveUrl();
    setActive(null);
    setActiveError(null);
  }, [revokeActiveUrl]);

  return (
    <Card data-testid="kyc-doc-viewer">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base font-serif text-slate-900">
          KYC Documents
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchDocs}
          disabled={loading}
          className="border-slate-300 text-slate-700"
          data-testid="kyc-docs-refresh"
        >
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>

      <CardContent className="space-y-3">
        {loading && (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="kyc-docs-loading">
            Loading documents…
          </p>
        )}

        {!loading && error && (
          <div
            className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            data-testid="kyc-docs-error"
          >
            <AlertTriangle size={15} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && docs.length === 0 && (
          <div
            className="flex flex-col items-center gap-1 py-8 text-center text-slate-500"
            data-testid="kyc-docs-empty"
          >
            <Inbox size={22} className="text-slate-300" />
            <p className="text-sm">No KYC documents on file.</p>
          </div>
        )}

        {!loading && !error && docs.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-md border border-slate-200" data-testid="kyc-docs-list">
            {docs.map((doc) => {
              const kind = kindFromName(doc.file_name);
              return (
                <li
                  key={doc.id}
                  className="flex items-center justify-between gap-3 px-3 py-2.5"
                  data-testid="kyc-doc-row"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <KindIcon kind={kind} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {prettyType(doc.document_type)}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {doc.file_name || "—"} · Uploaded {fmtDateTime(doc.uploaded_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${statusPill(doc.status)}`}
                      data-testid="kyc-doc-status"
                    >
                      {doc.status || "unknown"}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDoc(doc)}
                      className="border-slate-300 text-slate-700"
                      data-testid="kyc-doc-open"
                    >
                      <Eye size={14} className="mr-1.5" />
                      View
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {active && (
          <div
            className="rounded-md border border-slate-200 bg-slate-50 p-3"
            data-testid="kyc-doc-preview"
          >
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <KindIcon kind={active.kind} />
                <p className="text-sm font-medium text-slate-900 truncate">
                  {active.doc.file_name || prettyType(active.doc.document_type)}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {active.url && (
                  <a
                    href={active.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-white"
                    data-testid="kyc-doc-open-tab"
                  >
                    <Download size={13} className="mr-1.5" />
                    Open in new tab
                  </a>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={closeDoc}
                  className="border-slate-300 text-slate-700"
                  data-testid="kyc-doc-close"
                  aria-label="Close document preview"
                >
                  <X size={14} />
                </Button>
              </div>
            </div>

            {activeLoading && (
              <p className="py-10 text-center text-sm text-slate-500" data-testid="kyc-doc-preview-loading">
                Loading document…
              </p>
            )}

            {!activeLoading && activeError && (
              <div
                className="flex items-center gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900"
                data-testid="kyc-doc-preview-error"
              >
                <AlertTriangle size={15} className="shrink-0" />
                <span>{activeError}</span>
              </div>
            )}

            {!activeLoading && !activeError && active.url && active.kind === "image" && (
              <img
                src={active.url}
                alt={active.doc.file_name || "KYC document"}
                className="max-h-[70vh] w-auto mx-auto rounded border border-slate-200 bg-white"
                data-testid="kyc-doc-image"
              />
            )}

            {!activeLoading && !activeError && active.url && active.kind === "pdf" && (
              <iframe
                src={active.url}
                title={active.doc.file_name || "KYC document"}
                className="w-full h-[70vh] rounded border border-slate-200 bg-white"
                data-testid="kyc-doc-pdf"
              />
            )}

            {!activeLoading && !activeError && active.url && active.kind === "other" && (
              <p className="py-8 text-center text-sm text-slate-500" data-testid="kyc-doc-unsupported">
                This file type can't be previewed inline. Use “Open in new tab” to download it.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KycDocViewer;
